/**
 * computeDashboard.ts
 *
 * Mini calculation engine for the Capacity Planner demo.
 * Mirrors the Python backend 5-stage pipeline:
 *
 *   Stage 1 — Forecast bottles → line-hours per room per month
 *   Stage 2 — Changeover hours (rolling average) distributed across months
 *   Stage 3 — Combine in-run + changeover hours per category per month
 *   Stage 4 — HC = (in-run labour + CO labour) × (1 + downtime) / available_hours
 *   Stage 5 — Assemble full DashboardData response
 */

import { DashboardData, HcBreakdownMonth, RoomData, RoomMonthData } from '../store/types';
import {
  ForecastRow,
  MOCK_CALENDAR,
  MOCK_CHANGEOVER_SUMMARY,
  MOCK_IN_RUN_CREWING,
  MOCK_INDIRECT_SUMMARY,
  MOCK_JOB_HISTORY,
  MOCK_PRODUCTS,
  MOCK_ROOMS,
  buildCalendar,
  generateForecast,
} from './mockData';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Nested dict helper — ensures a path exists and returns the leaf. */
function ensure<T>(
  obj: Record<string, Record<string, T>>,
  outer: string,
  inner: string,
  defaultVal: () => T,
): T {
  if (!obj[outer]) obj[outer] = {};
  if (obj[outer][inner] === undefined) obj[outer][inner] = defaultVal();
  return obj[outer][inner];
}

function zeroRoomMonth(): RoomMonthData {
  return { line_hours: 0, pa_hc: 0, mo_hc: 0, qi_hc: 0, total_hc: 0 };
}

/** Round to 2 decimal places — keeps numbers readable in the UI. */
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ---------------------------------------------------------------------------
// Build lookup maps from static reference data
// ---------------------------------------------------------------------------

/** roomName → category ('HIGH' | 'LOW' | 'OVER' | 'SAPS') */
const ROOM_CATEGORY: Record<string, string> = {};
/** roomName → room_short */
const ROOM_SHORT: Record<string, string> = {};

MOCK_ROOMS.forEach((r) => {
  ROOM_CATEGORY[r.room_long] = r.room_category;
  ROOM_SHORT[r.room_long]    = r.room_short;
});

/** roomName → room_type_description ("High Speed 1", "High Speed 2", etc.) */
const ROOM_TYPE_DESC: Record<string, string> = {};
MOCK_ROOMS.forEach((r) => {
  ROOM_TYPE_DESC[r.room_long] = r.room_type_description;
});

/** NDC11 → product record */
const PRODUCT_BY_NDC: Record<string, (typeof MOCK_PRODUCTS)[0]> = {};
MOCK_PRODUCTS.forEach((p) => { PRODUCT_BY_NDC[p.NDC11] = p; });

// ---------------------------------------------------------------------------
// Stage 4 helper — compute headcount for one room-month
// ---------------------------------------------------------------------------

function computeRoomHC(
  lineHours:      number,
  coHours:        number,
  roomTypeDesc:   string,
  workDays:       number,
  params:         Record<string, number>,
): RoomMonthData {
  const crewing = MOCK_IN_RUN_CREWING[roomTypeDesc] ?? MOCK_IN_RUN_CREWING['Low Speed 1'];
  const {
    downtime_factor      = 0.20,
    hours_per_shift_room = 7.50,
    shifts_per_day       = 2,
  } = params;

  // Available machine-hours this month
  const availableHoursPerMonth = workDays * shifts_per_day * hours_per_shift_room;
  if (availableHoursPerMonth === 0 || lineHours === 0) return zeroRoomMonth();

  // Utilisation: what fraction of the month's capacity is consumed?
  // Values > 1.0 mean the room is over-loaded.
  const util = (lineHours + coHours) / availableHoursPerMonth;

  // Headcount model:
  //   HC = crewing_per_room × util × (1 + downtime_factor)
  //
  // util drives proportional headcount — lighter months (fewer work days or
  // lower forecast) correctly show lower HC.  Downtime factor adds a labour
  // buffer for unplanned stops.
  const loadFactor = util * (1 + downtime_factor);

  const paHc = Math.ceil(crewing.PA * loadFactor);
  const moHc = Math.ceil(crewing.MO * loadFactor);
  const qiHc = Math.ceil(crewing.QI * loadFactor);

  return {
    line_hours: r2(lineHours),
    pa_hc:      paHc,
    mo_hc:      moHc,
    qi_hc:      qiHc,
    total_hc:   paHc + moHc + qiHc,
  };
}

// ---------------------------------------------------------------------------
// Stage 5 helper — build HC breakdown (indirect/fixed staff) per month
// ---------------------------------------------------------------------------

function buildHcBreakdown(
  grandTotal:  Record<string, RoomMonthData>,
  params:      Record<string, number>,
  calendar:    { month: string; work_days: number }[],
): Record<string, HcBreakdownMonth> {
  const {
    pa_leads          = 2,
    mo_leads          = 2,
    qi_leads          = 2,
    aggregation_staff = 2,
    shift_coverage    = 2,
    qls               = 1,
  } = params;

  const breakdown: Record<string, HcBreakdownMonth> = {};

  calendar.forEach((cal) => {
    const m = cal.month;
    const gt = grandTotal[m] ?? zeroRoomMonth();

    // Direct operators come from grand total
    const pa    = r2(gt.pa_hc);
    const mo    = r2(gt.mo_hc);
    const qi    = r2(gt.qi_hc);

    // Fixed / indirect staff (applied as whole-department additions)
    const paLead   = pa_leads;
    const moLead   = mo_leads;
    const qiLead   = qi_leads;
    const agg      = aggregation_staff;
    const shiftCov = shift_coverage;
    const qlsVal   = qls;

    const packagingTotal = r2(pa + paLead + mo + moLead + agg + shiftCov);
    const qualityTotal   = r2(qi + qiLead + qlsVal);

    breakdown[m] = {
      pa,
      pa_lead:          paLead,
      mo,
      mo_lead:          moLead,
      aggregation:      agg,
      shift_coverage:   shiftCov,
      packaging_total:  packagingTotal,
      qi,
      qi_lead:          qiLead,
      qls:              qlsVal,
      quality_total:    qualityTotal,
    };
  });

  return breakdown;
}

// ---------------------------------------------------------------------------
// Stage 3 helper — capacity_by_room_type (line-hours per category per month)
// ---------------------------------------------------------------------------

function buildCapacityByRoomType(
  lineHoursByCatMonth: Record<string, Record<string, number>>,
): Record<string, Record<string, number>> {
  const result: Record<string, Record<string, number>> = {};
  Object.entries(lineHoursByCatMonth).forEach(([cat, months]) => {
    result[cat] = {};
    Object.entries(months).forEach(([month, hours]) => {
      result[cat][month] = r2(hours);
    });
  });
  return result;
}

// ---------------------------------------------------------------------------
// Forecast-jobs estimate from job history rolling average
// ---------------------------------------------------------------------------

function buildForecastJobs(
  params:   Record<string, number>,
  calendar: { month: string; work_days: number }[],
): Record<string, number> {
  const lookback = Math.min(
    Math.round(params['lookback_months'] ?? 6),
    MOCK_JOB_HISTORY.length,
  );
  const slice = MOCK_JOB_HISTORY.slice(-lookback);
  const avgJobs = slice.reduce((s, r) => s + r.jobs, 0) / slice.length;
  const overridePct = (params['forecast_jobs_override_pct'] ?? 0) / 100;
  const adjustedJobs = avgJobs * (1 + overridePct);

  const result: Record<string, number> = {};
  calendar.forEach((cal) => {
    result[cal.month] = r2(adjustedJobs);
  });
  return result;
}

// ---------------------------------------------------------------------------
// Crewing factors — bottles/day context for the dashboard header cards
// ---------------------------------------------------------------------------

function buildCrewingFactors(
  bottlesByMonth: Record<string, number>,
  calendar:       { month: string; work_days: number }[],
): Record<string, import('../store/types').CrewingFactorMonth> {
  const result: Record<string, import('../store/types').CrewingFactorMonth> = {};
  calendar.forEach((cal) => {
    const totalBottles = bottlesByMonth[cal.month] ?? 0;
    const bpd = cal.work_days > 0 ? r2(totalBottles / cal.work_days) : 0;
    result[cal.month] = {
      work_days:       cal.work_days,
      forecast_bottles: totalBottles,
      bottles_per_wkday: bpd,
    };
  });
  return result;
}

// ---------------------------------------------------------------------------
// Main exported function
// ---------------------------------------------------------------------------

/**
 * computeDashboard
 *
 * Accepts a forecast (array of NDC × month × bottles rows) and the current
 * planning parameters, and returns the full DashboardData object that the
 * UI renders.
 */
export function computeDashboard(
  forecast:       ForecastRow[],
  params:         Record<string, number>,
  startYearMonth?: string,
): DashboardData {
  const calendar = startYearMonth ? buildCalendar(startYearMonth) : MOCK_CALENDAR;
  const months   = calendar.map((c) => c.month);

  // ------------------------------------------------------------------
  // Stage 2 helper — pre-compute changeover hours per category
  // (defined here so it closes over the local `calendar`)
  // ------------------------------------------------------------------

  /**
   * Returns: { category: { month: totalCoHours } }
   * Distributes annual changeover hours weighted by work-day count.
   */
  function buildChangeoverHours(): Record<string, Record<string, number>> {
    const totalWorkDays = calendar.reduce((s, c) => s + c.work_days, 0);

    const minutesByCategory: Record<string, number> = {};
    MOCK_CHANGEOVER_SUMMARY.forEach((row) => {
      if (!minutesByCategory[row.room_category]) minutesByCategory[row.room_category] = 0;
      minutesByCategory[row.room_category] += row.total_r2_minutes;
    });

    const result: Record<string, Record<string, number>> = {};
    Object.entries(minutesByCategory).forEach(([cat, totalMins]) => {
      result[cat] = {};
      calendar.forEach((cal) => {
        const monthFraction = cal.work_days / totalWorkDays;
        result[cat][cal.month] = r2((totalMins / 60) * monthFraction);
      });
    });
    return result;
  }

  const CO_HOURS_BY_CAT_MONTH = buildChangeoverHours();

  // ------------------------------------------------------------------
  // Stage 1 — aggregate line-hours per room per month
  // ------------------------------------------------------------------

  // roomName → month → line-hours (machine time)
  const lineHoursByRoomMonth: Record<string, Record<string, number>> = {};
  // category → month → line-hours (for capacity_by_room_type)
  const lineHoursByCatMonth:  Record<string, Record<string, number>> = {};
  // room_type_description → month → line-hours (for display in capacity_by_room_type)
  const lineHoursByTypeMonth: Record<string, Record<string, number>> = {};
  // room_type_description → month → bottles (for utilization table)
  const bottlesByRoomType:    Record<string, Record<string, number>> = {};
  // month → total bottles (for crewing factors)
  const totalBottlesByMonth:  Record<string, number> = {};

  forecast.forEach((row) => {
    const product = PRODUCT_BY_NDC[row.NDC11];
    if (!product) return;                    // unknown NDC — skip

    const { RoomName, BPM } = product;
    const category = ROOM_CATEGORY[RoomName] ?? 'OTHER';
    const month    = row.ForecastPeriod;

    // hours = bottles / (bpm × 60)
    const hours = BPM > 0 ? row.ForecastBottles / (BPM * 60) : 0;

    // accumulate
    ensure(lineHoursByRoomMonth, RoomName, month, () => 0);
    lineHoursByRoomMonth[RoomName][month] += hours;

    ensure(lineHoursByCatMonth, category, month, () => 0);
    lineHoursByCatMonth[category][month] += hours;

    const roomTypeDesc = ROOM_TYPE_DESC[RoomName] ?? category;
    ensure(lineHoursByTypeMonth, roomTypeDesc, month, () => 0);
    lineHoursByTypeMonth[roomTypeDesc][month] += hours;

    ensure(bottlesByRoomType, roomTypeDesc, month, () => 0);
    bottlesByRoomType[roomTypeDesc][month] += row.ForecastBottles;

    if (!totalBottlesByMonth[month]) totalBottlesByMonth[month] = 0;
    totalBottlesByMonth[month] += row.ForecastBottles;
  });

  // ------------------------------------------------------------------
  // Stage 2+3 — for each room+month, combine line + CO hours → HC
  // ------------------------------------------------------------------

  // Work-day lookup
  const workDaysByMonth: Record<string, number> = {};
  calendar.forEach((c) => { workDaysByMonth[c.month] = c.work_days; });

  // roomName → RoomData
  const roomMap: Record<string, RoomData> = {};

  MOCK_ROOMS.forEach((room) => {
    const category = ROOM_CATEGORY[room.room_long] ?? 'OTHER';
    if (category === 'SAPS') return;          // SAPS not shown in main grid

    const monthsData: Record<string, RoomMonthData> = {};

    months.forEach((month) => {
      const lineHours = lineHoursByRoomMonth[room.room_long]?.[month] ?? 0;
      const coHours   = CO_HOURS_BY_CAT_MONTH[category]?.[month]      ?? 0;
      // Distribute CO hours equally among all rooms in the category
      const roomsInCat = MOCK_ROOMS.filter((r) => ROOM_CATEGORY[r.room_long] === category).length || 1;
      const coPerRoom  = coHours / roomsInCat;

      monthsData[month] = computeRoomHC(
        lineHours,
        coPerRoom,
        ROOM_TYPE_DESC[room.room_long] ?? 'Low Speed 1',
        workDaysByMonth[month] ?? 21,
        params,
      );
    });

    // Only include rooms that have at least some line-hours
    const hasActivity = months.some((m) => (monthsData[m]?.line_hours ?? 0) > 0);
    if (hasActivity) {
      roomMap[room.room_long] = {
        room_name:     room.room_long,
        room_short:    room.room_short,
        room_category: category as RoomData['room_category'],
        room_type:     ROOM_TYPE_DESC[room.room_long] ?? category,
        months:        monthsData,
      };
    }
  });

  const rooms = Object.values(roomMap);

  // ------------------------------------------------------------------
  // Stage 4 — category totals + grand total
  // ------------------------------------------------------------------

  const categoryTotals: Record<string, Record<string, RoomMonthData>> = {};
  const grandTotal:     Record<string, RoomMonthData>                  = {};

  months.forEach((month) => { grandTotal[month] = zeroRoomMonth(); });

  rooms.forEach((room) => {
    const cat = room.room_category;
    if (!categoryTotals[cat]) {
      categoryTotals[cat] = {};
      months.forEach((month) => { categoryTotals[cat][month] = zeroRoomMonth(); });
    }
    months.forEach((month) => {
      const rm = room.months[month];
      if (!rm) return;

      const catRow = categoryTotals[cat][month];
      catRow.line_hours = r2(catRow.line_hours + rm.line_hours);
      catRow.pa_hc      = r2(catRow.pa_hc      + rm.pa_hc);
      catRow.mo_hc      = r2(catRow.mo_hc      + rm.mo_hc);
      catRow.qi_hc      = r2(catRow.qi_hc      + rm.qi_hc);
      catRow.total_hc   = r2(catRow.total_hc   + rm.total_hc);

      const gt = grandTotal[month];
      gt.line_hours = r2(gt.line_hours + rm.line_hours);
      gt.pa_hc      = r2(gt.pa_hc      + rm.pa_hc);
      gt.mo_hc      = r2(gt.mo_hc      + rm.mo_hc);
      gt.qi_hc      = r2(gt.qi_hc      + rm.qi_hc);
      gt.total_hc   = r2(gt.total_hc   + rm.total_hc);
    });
  });

  // ------------------------------------------------------------------
  // Stage 5 — assemble remaining slices
  // ------------------------------------------------------------------

  const forecastJobs        = buildForecastJobs(params, calendar);
  const crewingFactors      = buildCrewingFactors(totalBottlesByMonth, calendar);
  const capacityByRoomType  = buildCapacityByRoomType(lineHoursByTypeMonth);
  const hcBreakdown         = buildHcBreakdown(grandTotal, params, calendar);
  // Round bottles by room type
  const bottlesByRoomTypeR2: Record<string, Record<string, number>> = {};
  Object.entries(bottlesByRoomType).forEach(([type, months]) => {
    bottlesByRoomTypeR2[type] = {};
    Object.entries(months).forEach(([month, bottles]) => {
      bottlesByRoomTypeR2[type][month] = Math.round(bottles);
    });
  });

  // changeover_data — CO hours per category per month (for detail table)
  const changeoverData: Record<string, Record<string, number>> = {};
  Object.entries(CO_HOURS_BY_CAT_MONTH).forEach(([cat, monthMap]) => {
    changeoverData[cat] = { ...monthMap };
  });

  return {
    months,
    rooms,
    category_totals:      categoryTotals,
    grand_total:          grandTotal,
    forecast_jobs:        forecastJobs,
    crewing_factors:      crewingFactors,
    capacity_by_room_type: capacityByRoomType,
    bottles_by_room_type:  bottlesByRoomTypeR2,
    hc_breakdown:         hcBreakdown,
    changeover_data:      changeoverData,
    summary: {
      total_rooms_active: rooms.length,
      planning_params:    { ...params },
    },
    indirect_summary: MOCK_INDIRECT_SUMMARY,
  };
}

// ---------------------------------------------------------------------------
// Re-export generateForecast so the store can call it without knowing mockData
// ---------------------------------------------------------------------------
export { generateForecast };
